/**
 * Friend Service - Handles all friend-related operations
 * Including friend requests, friend lists, and recent opponents
 */

import { database } from './firebase';
import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  serverTimestamp,
  push,
  query,
  orderByChild,
  equalTo
} from 'firebase/database';
import { createNotification, NOTIFICATION_TYPES } from './notificationService';

/**
 * Searches for a user by email
 *
 * @param {string} email - Email to search for
 * @returns {Promise<Object|null>} User object or null if not found
 */
export const searchUserByEmail = async (email) => {
  try {
    const usersRef = ref(database, 'users');
    const userQuery = query(usersRef, orderByChild('email'), equalTo(email.toLowerCase()));
    const snapshot = await get(userQuery);

    if (!snapshot.exists()) {
      return null;
    }

    let foundUser = null;
    snapshot.forEach((childSnapshot) => {
      foundUser = {
        userId: childSnapshot.key,
        ...childSnapshot.val()
      };
    });

    return foundUser;
  } catch (error) {
    console.error('Error searching user:', error);
    throw error;
  }
};

/**
 * Sends a friend request to another user
 *
 * @param {string} fromUserId - User ID sending the request
 * @param {string} fromUserName - Display name of sender
 * @param {string} fromUserPhoto - Photo URL of sender
 * @param {string} toUserId - User ID receiving the request
 * @param {string} toUserName - Display name of recipient
 * @param {boolean} allowResend - If true, updates existing request instead of throwing error
 * @returns {Promise<void>}
 */
export const sendFriendRequest = async (
  fromUserId,
  fromUserName,
  fromUserPhoto,
  toUserId,
  toUserName,
  allowResend = false
) => {
  try {
    // Check if already friends
    const friendshipExists = await areFriends(fromUserId, toUserId);
    if (friendshipExists) {
      throw new Error('Already friends with this user');
    }

    // Check if request already exists
    const requestExists = await hasPendingRequest(fromUserId, toUserId);
    if (requestExists && !allowResend) {
      throw new Error('Friend request already sent');
    }

    // Create or update friend request with new timestamp
    const requestRef = ref(database, `friendRequests/${toUserId}/${fromUserId}`);
    await set(requestRef, {
      fromUserId,
      fromUserName,
      fromUserPhoto,
      toUserId,
      toUserName,
      status: 'pending',
      timestamp: Date.now(),
      createdAt: serverTimestamp()
    });

    // Send notification (always send a new one when re-sending)
    try {
      await createNotification(toUserId, {
        type: NOTIFICATION_TYPES.FRIEND_REQUEST,
        title: 'Friend Request',
        message: `${fromUserName} sent you a friend request`,
        data: { fromUserId },
        actionUrl: '/profile'
      });
    } catch (notificationError) {
      console.error('Error sending friend request notification:', notificationError);
    }
  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

/**
 * Accepts a friend request
 *
 * @param {string} userId - User ID accepting the request
 * @param {string} userName - Display name of user accepting
 * @param {string} userPhoto - Photo URL of user accepting
 * @param {string} friendId - User ID who sent the request
 * @param {string} friendName - Display name of friend
 * @param {string} friendPhoto - Photo URL of friend
 * @returns {Promise<void>}
 */
export const acceptFriendRequest = async (
  userId,
  userName,
  userPhoto,
  friendId,
  friendName,
  friendPhoto
) => {
  try {
    const timestamp = Date.now();

    // Add to both users' friend lists
    const user1FriendRef = ref(database, `friends/${userId}/${friendId}`);
    await set(user1FriendRef, {
      userId: friendId,
      displayName: friendName,
      photoURL: friendPhoto,
      status: 'accepted',
      since: timestamp,
      addedAt: serverTimestamp()
    });

    const user2FriendRef = ref(database, `friends/${friendId}/${userId}`);
    await set(user2FriendRef, {
      userId: userId,
      displayName: userName,
      photoURL: userPhoto,
      status: 'accepted',
      since: timestamp,
      addedAt: serverTimestamp()
    });

    // Remove the friend request
    const requestRef = ref(database, `friendRequests/${userId}/${friendId}`);
    await remove(requestRef);

    // Send notification to the requester
    try {
      await createNotification(friendId, {
        type: NOTIFICATION_TYPES.FRIEND_REQUEST,
        title: 'Friend Request Accepted',
        message: `${userName} accepted your friend request`,
        data: { userId },
        actionUrl: '/profile'
      });
    } catch (notificationError) {
      console.error('Error sending acceptance notification:', notificationError);
    }
  } catch (error) {
    console.error('Error accepting friend request:', error);
    throw error;
  }
};

/**
 * Declines a friend request
 *
 * @param {string} userId - User ID declining the request
 * @param {string} friendId - User ID who sent the request
 * @returns {Promise<void>}
 */
export const declineFriendRequest = async (userId, friendId) => {
  try {
    const requestRef = ref(database, `friendRequests/${userId}/${friendId}`);
    await remove(requestRef);
  } catch (error) {
    console.error('Error declining friend request:', error);
    throw error;
  }
};

/**
 * Cancels/withdraws a sent friend request
 *
 * @param {string} fromUserId - User ID who sent the request (wants to cancel)
 * @param {string} toUserId - User ID who received the request
 * @returns {Promise<void>}
 */
export const cancelFriendRequest = async (fromUserId, toUserId) => {
  try {
    const requestRef = ref(database, `friendRequests/${toUserId}/${fromUserId}`);
    await remove(requestRef);
  } catch (error) {
    console.error('Error canceling friend request:', error);
    throw error;
  }
};

/**
 * Removes a friend
 *
 * @param {string} userId - Current user's ID
 * @param {string} friendId - Friend's user ID
 * @returns {Promise<void>}
 */
export const removeFriend = async (userId, friendId) => {
  try {
    // Remove from both users' friend lists
    const user1FriendRef = ref(database, `friends/${userId}/${friendId}`);
    const user2FriendRef = ref(database, `friends/${friendId}/${userId}`);

    await Promise.all([
      remove(user1FriendRef),
      remove(user2FriendRef)
    ]);
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

/**
 * Gets all friends for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of friends
 */
export const getFriends = async (userId) => {
  try {
    const friendsRef = ref(database, `friends/${userId}`);
    const snapshot = await get(friendsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const friends = [];
    snapshot.forEach((childSnapshot) => {
      friends.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    friends.sort((a, b) => (b.since || 0) - (a.since || 0));

    return friends;
  } catch (error) {
    console.error('Error getting friends:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time friend list updates
 *
 * @param {string} userId - User ID
 * @param {Function} callback - Function to call when friends change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToFriends = (userId, callback) => {
  const friendsRef = ref(database, `friends/${userId}`);

  const unsubscribe = onValue(friendsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const friends = [];
    snapshot.forEach((childSnapshot) => {
      friends.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    friends.sort((a, b) => (b.since || 0) - (a.since || 0));

    callback(friends);
  });

  return () => off(friendsRef, 'value', unsubscribe);
};

/**
 * Gets all pending friend requests for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of friend requests
 */
export const getFriendRequests = async (userId) => {
  try {
    const requestsRef = ref(database, `friendRequests/${userId}`);
    const snapshot = await get(requestsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const requests = [];
    snapshot.forEach((childSnapshot) => {
      requests.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    requests.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return requests;
  } catch (error) {
    console.error('Error getting friend requests:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time friend request updates
 *
 * @param {string} userId - User ID
 * @param {Function} callback - Function to call when requests change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToFriendRequests = (userId, callback) => {
  const requestsRef = ref(database, `friendRequests/${userId}`);

  const unsubscribe = onValue(requestsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const requests = [];
    snapshot.forEach((childSnapshot) => {
      requests.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    requests.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    callback(requests);
  });

  return () => off(requestsRef, 'value', unsubscribe);
};

/**
 * Checks if two users are friends
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {Promise<boolean>} True if friends, false otherwise
 */
export const areFriends = async (userId1, userId2) => {
  try {
    const friendRef = ref(database, `friends/${userId1}/${userId2}`);
    const snapshot = await get(friendRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking friendship:', error);
    return false;
  }
};

/**
 * Checks if there's a pending friend request
 *
 * @param {string} fromUserId - User who sent the request
 * @param {string} toUserId - User who received the request
 * @returns {Promise<boolean>} True if pending request exists
 */
export const hasPendingRequest = async (fromUserId, toUserId) => {
  try {
    const requestRef = ref(database, `friendRequests/${toUserId}/${fromUserId}`);
    const snapshot = await get(requestRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking pending request:', error);
    return false;
  }
};

/**
 * Adds a user to recent opponents list
 *
 * @param {string} userId - Current user's ID
 * @param {string} opponentId - Opponent's user ID
 * @param {string} opponentName - Opponent's display name
 * @param {string} opponentPhoto - Opponent's photo URL
 * @param {string} gameType - Type of game played
 * @param {string} result - Result of the game (won/lost/draw)
 * @returns {Promise<void>}
 */
export const addRecentOpponent = async (
  userId,
  opponentId,
  opponentName,
  opponentPhoto,
  gameType,
  result
) => {
  try {
    const opponentRef = ref(database, `recentOpponents/${userId}/${opponentId}`);

    // Get existing data
    const snapshot = await get(opponentRef);
    const existingData = snapshot.exists() ? snapshot.val() : {};

    // Update or create
    await set(opponentRef, {
      userId: opponentId,
      displayName: opponentName,
      photoURL: opponentPhoto,
      lastPlayed: Date.now(),
      lastPlayedAt: serverTimestamp(),
      lastGame: gameType,
      lastResult: result,
      gamesPlayed: (existingData.gamesPlayed || 0) + 1
    });
  } catch (error) {
    console.error('Error adding recent opponent:', error);
  }
};

/**
 * Gets recent opponents for a user
 *
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of opponents to return
 * @returns {Promise<Array>} Array of recent opponents
 */
export const getRecentOpponents = async (userId, limit = 10) => {
  try {
    const opponentsRef = ref(database, `recentOpponents/${userId}`);
    const snapshot = await get(opponentsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const opponents = [];
    snapshot.forEach((childSnapshot) => {
      opponents.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    opponents.sort((a, b) => (b.lastPlayed || 0) - (a.lastPlayed || 0));

    return opponents.slice(0, limit);
  } catch (error) {
    console.error('Error getting recent opponents:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time recent opponents updates
 *
 * @param {string} userId - User ID
 * @param {Function} callback - Function to call when opponents change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToRecentOpponents = (userId, callback) => {
  const opponentsRef = ref(database, `recentOpponents/${userId}`);

  const unsubscribe = onValue(opponentsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const opponents = [];
    snapshot.forEach((childSnapshot) => {
      opponents.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by most recent first
    opponents.sort((a, b) => (b.lastPlayed || 0) - (a.lastPlayed || 0));

    callback(opponents.slice(0, 10));
  });

  return () => off(opponentsRef, 'value', unsubscribe);
};

/**
 * Gets the count of pending friend requests
 *
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of pending requests
 */
export const getPendingRequestCount = async (userId) => {
  try {
    const requestsRef = ref(database, `friendRequests/${userId}`);
    const snapshot = await get(requestsRef);

    if (!snapshot.exists()) {
      return 0;
    }

    let count = 0;
    snapshot.forEach(() => {
      count++;
    });

    return count;
  } catch (error) {
    console.error('Error getting pending request count:', error);
    return 0;
  }
};
